import { api, type IncomeRecord, queryKeys } from '@alpha/api-client';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, formatLds, formatNumber, formatUsd, TOKEN_LABEL } from '../../lib/format';
import { Card, Chip, ProgressBar, Spinner } from '../../ui';
import { INCOME_TABS } from './incomeTabs';

const col = createColumnHelper<IncomeRecord>();

/**
 * Alpha Club B — seven ranks (B1..B7). Qualification runs through ALPHA
 * members in the network who have each completed 10 directs, so the counts
 * here are ALPHA-member counts, not raw downline size.
 */
export function AlphaClubB() {
  const { query, setPage } = useTableQuery();

  const tiers = useQuery({
    queryKey: ['alpha-club-b-progress'],
    queryFn: () => api.income.alphaClubBProgress().then((r) => r.data),
  });

  const ledger = useQuery({
    queryKey: queryKeys.income('alpha_club_b', query),
    queryFn: () => api.income.list('alpha_club_b', query),
  });

  const columns = useMemo(
    () => [
      col.accessor('description', { header: 'Rank', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('amount_usd', { header: 'Gift (USD)', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('amount_lds', { header: `Gift (${TOKEN_LABEL})`, cell: (i) => formatLds(i.getValue()) }),
      col.accessor('created_at', { header: 'Awarded', cell: (i) => formatDateTime(i.getValue()) }),
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="Alpha Club B"
        description="Rank gifts earned as qualifying ALPHA members build in your network — B1 through B7."
        tabs={INCOME_TABS}
      />

      <Card>
        <h3 className="card-title">Tier progress</h3>
        {tiers.isLoading ? (
          <Spinner />
        ) : (
          <ul className="tier-list">
            {tiers.data?.map((tier) => (
              <li key={tier.tier} className="tier-row">
                <div className="tier-head">
                  <strong>{tier.rank_code}</strong>
                  {tier.achieved ? <Chip tone="success">Achieved</Chip> : <Chip tone="gold">Locked</Chip>}
                  <span className="text-gold">
                    {formatUsd(tier.gift_usd)} <span className="text-muted small">({formatLds(tier.gift_lds)})</span>
                  </span>
                </div>
                <p className="text-muted small">{tier.requirement_label}</p>
                <ProgressBar
                  value={tier.achieved_count}
                  max={tier.required_count}
                  label={`${formatNumber(tier.achieved_count)} / ${formatNumber(tier.required_count)}`}
                />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <DataTable<IncomeRecord>
        title="Alpha Club B Payouts"
        columns={columns}
        data={ledger.data?.data}
        meta={ledger.data?.meta}
        isLoading={ledger.isLoading}
        error={ledger.error as Error | null}
        emptyLabel="No Alpha Club B gifts awarded yet"
        onPageChange={setPage}
      />
    </>
  );
}
